import React, {createContext, FC, useMemo} from "react";
import {TodoContextType, TodoContextProviderProps} from "./types";
import {useFetchedResource} from "../api/hooks/useFetchedResource";
import {TodoService} from "../api/services/core/TodoService";
import {Todo, TodoEntityType} from "../api/dto/Todo";
import {useRefreshEffect} from "../realtime/RefreshController";

export const TodoContext = createContext<TodoContextType>({} as TodoContextType)

export const TodoContextProvider: FC<TodoContextProviderProps> = (props) => {
  const navigate = props.navigateFactory()
  const todoService = useMemo(() => new TodoService(props.config, props.tokenResolver, navigate), [props.config, props.tokenResolver])

  const todos = useFetchedResource<Todo[]>(() => todoService.browse())

  useRefreshEffect('todo', () => {
    if (props.enabled) todos.reload(undefined)
  }, [props.enabled])

  const pendingCount = useMemo(() => (todos.resource ?? []).filter(t => !t.isDone).length, [todos.resource])

  const createTodo = async (text: string, entityType?: TodoEntityType, entityId?: string) => {
    await todoService.create(text, entityType, entityId)
    await todos.reload(undefined)
  }

  const toggleTodo = async (todo: Todo) => {
    // Nothing is changed optimistically before this (the checkbox reflects todo.isDone straight
    // from context state), so there's nothing to revert on failure — just avoid an unhandled
    // promise rejection.
    try {
      await todoService.update(todo.id, {isDone: !todo.isDone})
      await todos.reload(undefined)
    } catch {
      // Swallowed intentionally, see comment above.
    }
  }

  const updateTodoText = async (todo: Todo, text: string) => {
    await todoService.update(todo.id, {text})
    await todos.reload(undefined)
  }

  const deleteTodo = async (todoId: string) => {
    // The todo simply stays in the list on failure — nothing to revert, just avoid an unhandled
    // promise rejection.
    try {
      await todoService.delete(todoId)
      await todos.reload(undefined)
    } catch {
      // Swallowed intentionally, see comment above.
    }
  }

  return <TodoContext.Provider value={{
    todos: todos.resource ?? [],
    loading: todos.loading,
    error: todos.error,
    pendingCount,
    crmUrl: props.config.crmUrl,
    createTodo,
    toggleTodo,
    updateTodoText,
    deleteTodo,
  }}>{props.children}</TodoContext.Provider>
}
